import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceAPI, sellerProfileAPI, bankAccountAPI, customerProfileAPI, itemProfileAPI, noteTemplateAPI, billSettingsAPI } from '../../services/api';
import './InvoiceForm.css';

const InvoiceForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sellerProfiles, setSellerProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [customerProfiles, setCustomerProfiles] = useState([]);
  const [selectedCustomerProfile, setSelectedCustomerProfile] = useState('');
  const [itemProfiles, setItemProfiles]     = useState([]);
  const [noteTemplates, setNoteTemplates]   = useState([]);
  const [invoicePreview, setInvoicePreview] = useState('');
  const [selectedShippingProfile, setSelectedShippingProfile] = useState('');

  const [formData, setFormData] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    bank_account_id: '',
    seller_name: '',
    seller_address: '',
    seller_gstin: '',
    seller_contact: '',
    customer_name: '',
    billing_address: '',
    shipping_address: '',
    customer_gstin: '',
    state_name: '',
    state_code: '',
    note: '',
    service_period_from: '',
    service_period_to: '',
  });

  useEffect(() => {
    fetchSellerProfiles();
    fetchBankAccounts();
    fetchCustomerProfiles();
    fetchItemProfiles();
    fetchNoteTemplates();
    billSettingsAPI.get().then((res) => { if (res.success) setInvoicePreview(res.data.preview); }).catch(() => {});
  }, []);

  const fetchSellerProfiles = async () => {
    try {
      const response = await sellerProfileAPI.getAll();
      if (response.success) {
        setSellerProfiles(response.data);
        // Auto-select default profile
        const defaultProfile = response.data.find(p => p.is_default);
        if (defaultProfile) {
          handleProfileSelect(defaultProfile.id.toString());
        }
      }
    } catch (err) {
      console.error('Failed to fetch seller profiles:', err);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await bankAccountAPI.getAll();
      if (response.success) {
        setBankAccounts(response.data);
        // Auto-select default bank account
        const defaultAccount = response.data.find(a => a.is_default);
        if (defaultAccount) {
          handleBankAccountSelect(defaultAccount.id.toString());
        }
      }
    } catch (err) {
      console.error('Failed to fetch bank accounts:', err);
    }
  };

  const handleProfileSelect = (profileId) => {
    setSelectedProfile(profileId);
    if (profileId) {
      const profile = sellerProfiles.find(p => p.id.toString() === profileId);
      if (profile) {
        setFormData({
          ...formData,
          seller_name: profile.seller_name,
          seller_address: profile.seller_address,
          seller_gstin: profile.seller_gstin || '',
          seller_contact: profile.seller_contact || '',
        });
      }
    }
  };

  const handleBankAccountSelect = (accountId) => {
    setSelectedBankAccount(accountId);
    setFormData({
      ...formData,
      bank_account_id: accountId || '',
    });
  };

  const fetchNoteTemplates = async () => {
    try {
      const response = await noteTemplateAPI.getAll();
      if (response.success) setNoteTemplates(response.data);
    } catch (err) {
      console.error('Failed to fetch note templates:', err);
    }
  };

  const fetchItemProfiles = async () => {
    try {
      const response = await itemProfileAPI.getAll();
      if (response.success) setItemProfiles(response.data);
    } catch (err) {
      console.error('Failed to fetch item profiles:', err);
    }
  };

  const fetchCustomerProfiles = async () => {
    try {
      const response = await customerProfileAPI.getAll();
      if (response.success) {
        setCustomerProfiles(response.data);
        // Auto-select default profile
        const defaultProfile = response.data.find(p => p.is_default);
        if (defaultProfile) {
          handleCustomerProfileSelect(defaultProfile.id.toString());
        }
      }
    } catch (err) {
      console.error('Failed to fetch customer profiles:', err);
    }
  };

  const handleCustomerProfileSelect = (profileId) => {
    setSelectedCustomerProfile(profileId);
    if (profileId) {
      const profile = customerProfiles.find(p => p.id.toString() === profileId);
      if (profile) {
        setFormData({
          ...formData,
          customer_name: profile.customer_name,
          billing_address: profile.billing_address,
          shipping_address: '',
          customer_gstin: profile.customer_gstin || '',
          state_name: profile.state_name || '',
          state_code: profile.state_code || '',
        });
        setSelectedShippingProfile('');
      }
    }
  };

  const [items, setItems] = useState([
    {
      description: '',
      hsn_sac_code: '',
      quantity: 1,
      unit: 'Nos',
      unit_price: 0,
      discount: 0,
      gst_rate: 18,
      tax_type: 'exclusive',
    },
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleItemProfileSelect = (index, profileId) => {
    if (!profileId) return;
    const profile = itemProfiles.find((p) => p.id.toString() === profileId);
    if (!profile) return;
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      description:  profile.description,
      hsn_sac_code: profile.hsn_sac_code || '',
      unit:         profile.unit,
      unit_price:   profile.unit_price,
      gst_rate:     parseInt(profile.gst_rate, 10),
      tax_type:     profile.tax_type || 'exclusive',
    };
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        description: '',
        hsn_sac_code: '',
        quantity: 1,
        unit: 'Nos',
        unit_price: 0,
        discount: 0,
        gst_rate: 18,
        tax_type: 'exclusive',
      },
    ]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateItemTotal = (item) => {
    const base = item.quantity * item.unit_price - (item.discount || 0);
    if (item.tax_type === 'inclusive') {
      return base; // price already includes tax
    }
    return base + (base * item.gst_rate) / 100;
  };

  const calculateGrandTotal = () => {
    return items.reduce((total, item) => total + calculateItemTotal(item), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const invoiceData = {
        ...formData,
        items: items.map((item) => ({
          ...item,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          discount: parseFloat(item.discount || 0),
          gst_rate: parseFloat(item.gst_rate),
        })),
      };

      const response = await invoiceAPI.create(invoiceData);

      if (response.success) {
        alert('Invoice created successfully!');
        navigate('/dashboard/invoices');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="invoice-form-container">
      <div className="form-header">
        <h2>Create New Invoice</h2>
        <button type="button" onClick={() => navigate('/dashboard/invoices')} className="back-btn">
          Back to List
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Invoice Details */}
        <div className="form-section">
          <h3>Invoice Details</h3>
          <div className="form-row">
            <div className="form-field">
              <label>Invoice Number</label>
              <input
                type="text"
                value={invoicePreview || 'Auto-generated'}
                readOnly
                style={{ background: '#f3f4f6', color: '#374151', cursor: 'default', fontWeight: 600 }}
              />
            </div>
            <div className="form-field">
              <label>Invoice Date *</label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label>Service Period From</label>
              <input
                type="date"
                name="service_period_from"
                value={formData.service_period_from}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-field">
              <label>Service Period To</label>
              <input
                type="date"
                name="service_period_to"
                value={formData.service_period_to}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field full-width">
              <div className="section-header-with-action" style={{ marginBottom: 6 }}>
                <label style={{ margin: 0 }}>Note</label>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/invoice-settings?tab=note-templates')}
                  className="manage-profiles-btn"
                >
                  Manage Notes
                </button>
              </div>
              {noteTemplates.length > 0 && (
                <select
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const t = noteTemplates.find((n) => n.id.toString() === e.target.value);
                    if (t) setFormData({ ...formData, note: t.note });
                    e.target.value = '';
                  }}
                  className="profile-selector"
                  style={{ marginBottom: 6 }}
                  defaultValue=""
                >
                  <option value="">-- Select a saved note --</option>
                  {noteTemplates.map((t) => (
                    <option key={t.id} value={t.id}>{t.title}</option>
                  ))}
                </select>
              )}
              <textarea
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                rows="3"
                placeholder="Add any additional notes for this invoice"
              />
            </div>
          </div>
        </div>

        {/* Seller Details */}
        <div className="form-section">
          <div className="section-header-with-action">
            <h3>Seller Details</h3>
            <button
              type="button"
              onClick={() => navigate('/dashboard/invoice-settings?tab=seller-profiles')}
              className="manage-profiles-btn"
            >
              Manage Profiles
            </button>
          </div>

          <div className="form-row">
            <div className="form-field full-width">
              <label>Select Seller Profile</label>
              <select
                value={selectedProfile}
                onChange={(e) => handleProfileSelect(e.target.value)}
                className="profile-selector"
              >
                <option value="">-- Select a profile or enter manually --</option>
                {sellerProfiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.profile_name} - {profile.seller_name}
                    {profile.is_default && ' (Default)'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>Seller Name *</label>
              <input
                type="text"
                name="seller_name"
                value={formData.seller_name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-field">
              <label>GSTIN</label>
              <input
                type="text"
                name="seller_gstin"
                value={formData.seller_gstin}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field full-width">
              <label>Address *</label>
              <textarea
                name="seller_address"
                value={formData.seller_address}
                onChange={handleInputChange}
                required
                rows="2"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label>Contact</label>
              <input
                type="text"
                name="seller_contact"
                value={formData.seller_contact}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        {/* Bank Account Details */}
        <div className="form-section">
          <div className="section-header-with-action">
            <h3>Bank Account Details</h3>
            <button
              type="button"
              onClick={() => navigate('/dashboard/invoice-settings?tab=bank-accounts')}
              className="manage-profiles-btn"
            >
              Manage Accounts
            </button>
          </div>

          <div className="form-row">
            <div className="form-field full-width">
              <label>Select Bank Account (Optional)</label>
              <select
                value={selectedBankAccount}
                onChange={(e) => handleBankAccountSelect(e.target.value)}
                className="profile-selector"
              >
                <option value="">-- Select a bank account --</option>
                {bankAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.account_name} - {account.account_number}
                    {account.is_default && ' (Default)'}
                  </option>
                ))}
              </select>
              <small style={{ color: '#7f8c8d', marginTop: '5px', display: 'block' }}>
                Bank details will appear on the invoice if selected
              </small>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="form-section">
          <div className="section-header-with-action">
            <h3>Customer Details</h3>
            <button
              type="button"
              onClick={() => navigate('/dashboard/invoice-settings?tab=customer-profiles')}
              className="manage-profiles-btn"
            >
              Manage Profiles
            </button>
          </div>

          <div className="form-row">
            <div className="form-field full-width">
              <label>Select Customer Profile</label>
              <select
                value={selectedCustomerProfile}
                onChange={(e) => handleCustomerProfileSelect(e.target.value)}
                className="profile-selector"
              >
                <option value="">-- Select a profile or enter manually --</option>
                {customerProfiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.profile_name} - {profile.customer_name}
                    {profile.is_default && ' (Default)'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>Customer Name *</label>
              <input
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-field">
              <label>Customer GSTIN</label>
              <input
                type="text"
                name="customer_gstin"
                value={formData.customer_gstin}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field full-width">
              <label>Billing Address *</label>
              <textarea
                name="billing_address"
                value={formData.billing_address}
                onChange={handleInputChange}
                required
                rows="2"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field full-width">
              <label>Shipping Address</label>
              <select
                value={selectedShippingProfile}
                onChange={(e) => {
                  const profileId = e.target.value;
                  setSelectedShippingProfile(profileId);
                  if (profileId === '__same__') {
                    setFormData(prev => ({ ...prev, shipping_address: prev.billing_address }));
                  } else if (profileId) {
                    const profile = customerProfiles.find(p => p.id.toString() === profileId);
                    if (profile) setFormData(prev => ({ ...prev, shipping_address: profile.billing_address }));
                  } else {
                    setFormData(prev => ({ ...prev, shipping_address: '' }));
                  }
                }}
              >
                <option value="">-- No Shipping Address --</option>
                <option value="__same__">-- Same as Billing Address --</option>
                {customerProfiles.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.profile_name} — {p.customer_name}
                  </option>
                ))}
              </select>
              {formData.shipping_address && (
                <textarea
                  name="shipping_address"
                  value={formData.shipping_address}
                  onChange={handleInputChange}
                  rows="2"
                  style={{ marginTop: '6px' }}
                />
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label>State Name *</label>
              <input
                type="text"
                name="state_name"
                value={formData.state_name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-field">
              <label>State Code *</label>
              <input
                type="text"
                name="state_code"
                value={formData.state_code}
                onChange={handleInputChange}
                required
                placeholder="e.g., 27 for Maharashtra"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="form-section">
          <div className="section-header-with-action">
            <h3>Invoice Items</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => navigate('/dashboard/invoice-settings?tab=item-profiles')}
                className="manage-profiles-btn"
              >
                Manage Items
              </button>
              <button type="button" onClick={addItem} className="add-item-btn">
                + Add Item
              </button>
            </div>
          </div>

          {items.map((item, index) => (
            <div key={index} className="item-card">
              <div className="item-header">
                <h4>Item {index + 1}</h4>
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="remove-item-btn"
                  >
                    Remove
                  </button>
                )}
              </div>

              {itemProfiles.length > 0 && (
                <div className="form-row">
                  <div className="form-field full-width">
                    <label>Select Saved Item (optional)</label>
                    <select
                      onChange={(e) => handleItemProfileSelect(index, e.target.value)}
                      className="profile-selector"
                      defaultValue=""
                    >
                      <option value="">-- Select a saved item to auto-fill --</option>
                      {itemProfiles.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.profile_name} — {p.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-field full-width">
                  <label>Description *</label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>HSN/SAC Code *</label>
                  <input
                    type="text"
                    value={item.hsn_sac_code}
                    onChange={(e) => handleItemChange(index, 'hsn_sac_code', e.target.value)}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Unit</label>
                  <input
                    type="text"
                    value={item.unit}
                    onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Unit Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Discount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.discount}
                    onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>GST Rate (%) *</label>
                  <select
                    value={item.gst_rate}
                    onChange={(e) => handleItemChange(index, 'gst_rate', e.target.value)}
                    required
                  >
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Tax Amount</label>
                  <input
                    type="text"
                    readOnly
                    value={(() => {
                      const base = item.quantity * item.unit_price - (item.discount || 0);
                      const rate = parseFloat(item.gst_rate) || 0;
                      const tax  = item.tax_type === 'inclusive'
                        ? base * rate / (100 + rate)
                        : base * rate / 100;
                      return `₹${tax.toFixed(2)} (${item.tax_type === 'inclusive' ? 'Incl.' : 'Excl.'})`;
                    })()}
                    style={{ background: '#f9fafb', color: '#374151', cursor: 'default' }}
                  />
                </div>
              </div>

              <div className="item-total">
                Item Total: ₹{calculateItemTotal(item).toFixed(2)}
              </div>
            </div>
          ))}
        </div>

        {/* Grand Total */}
        <div className="total-section">
          <h3>Grand Total: ₹{calculateGrandTotal().toFixed(2)}</h3>
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <button type="button" onClick={() => navigate('/dashboard/invoices')} className="cancel-btn">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? 'Creating...' : 'Create Invoice'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;
